import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchViolenceRisk } from "../../../store/slices/violenceRisksSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function ViolenceFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const violenceState = useSelector((state) => state.violenceRisks || {});
    const { currentViolenceRisk, isLoading, error } = violenceState;

    useEffect(() => {
        if (id) dispatch(fetchViolenceRisk(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentViolenceRisk) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentViolenceRisk, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Violence Risk Assessment...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentViolenceRisk) return <div className="p-8 text-center">No record found.</div>;

    const d = currentViolenceRisk;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    const riskItems = [
        { id: "violent_acts", title: "Previous and/or current violence" },
        { id: "violent_threats", title: "Previous and/or current threats" },
        { id: "substance_misuse", title: "Previous and/or current substance abuse" },
        { id: "psychotic_symptoms", title: "Previous and/or current major mental illness" },
        { id: "personality_disorder", title: "Personality disorder" },
        { id: "non_compliance", title: "Lack of insight into illness/behaviour" },
        { id: "suspicion", title: "Expresses suspicion" },
        { id: "lack_empathy", title: "Shows lack of empathy" },
        { id: "unrealistic_planning", title: "Unrealistic planning" },
        { id: "future_stress", title: "Future stress-situations" }
    ];

    const getLabel = (val) => {
        if (val === "no") return "No";
        if (val === "maybe") return "Maybe / Moderate";
        if (val === "yes") return "Yes";
        if (val === "unknown") return "Do not know";
        return "-";
    };

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 12px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 15px !important; left: 20px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
                .header h6 { font-size: 1rem !important; text-decoration: underline !important; }

                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 8px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 20% !important; }

                .vrisk-table { width: 100% !important; border-collapse: collapse !important; margin-top: 10px !important; }
                .vrisk-table th, .vrisk-table td { border: 1px solid #000 !important; padding: 10px !important; vertical-align: middle !important; }
                .vrisk-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                .item-num { width: 40px !important; text-align: center !important; font-weight: bold !important; }
                .response-cell { width: 150px !important; text-align: center !important; font-weight: bold !important; text-transform: uppercase !important; }

                .evaluation-box { border: 2px solid #000 !important; margin-top: 25px !important; padding: 15px !important; background: #fafafa !important; page-break-inside: avoid !important; }
                .eval-title { font-weight: bold !important; font-size: 1.1rem !important; display: block !important; margin-bottom: 15px !important; border-bottom: 1px solid #000 !important; }
                
                .sig-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 50px !important; margin-top: 40px !important; }
                .sig-box { border-top: 1.5px solid #000 !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                
                .footer { margin-top: 30px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 10px !important; }
                
                @media print {
                    .vrisk-table tr { page-break-inside: avoid !important; }
                }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                    <div className="logo"><img src={logo} alt="Logo" /></div>
                    <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>V-RISK-10: VIOLENCE RISK SCREENING</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Patient Name</td>
                            <td>{d.patient_name || d.patientName}</td>
                            <td className="label-bg">Date</td>
                            <td>{fmtDate(d.date)}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Timing</td>
                            <td style={{ textTransform: 'capitalize' }}>{d.assessment_timing || d.assessmentTiming}</td>
                            <td className="label-bg">Case ID</td>
                            <td>{d.case_id}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="vrisk-table">
                    <thead>
                        <tr>
                            <th className="item-num">#</th>
                            <th>Risk Factors Checklist</th>
                            <th className="response-cell">Response</th>
                        </tr>
                    </thead>
                    <tbody>
                        {riskItems.map((item, index) => {
                            // Support for both camelCase and snake_case keys
                            const value = d[item.id] || d[item.id.replace(/_([a-z])/g, (g) => g[1].toUpperCase())];
                            return (
                                <tr key={item.id}>
                                    <td className="item-num">{index + 1}</td>
                                    <td>{item.title}</td>
                                    <td className={`response-cell ${value === 'yes' ? 'bg-light' : ''}`}>
                                        {getLabel(value)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div className="evaluation-box">
                    <span className="eval-title">OVERALL CLINICAL EVALUATION</span>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Greatness of Violence Risk:</strong>
                        <span style={{ marginLeft: '10px', fontSize: '1.2rem', textDecoration: 'underline', textTransform: 'uppercase' }}>
                            {d.overall_risk || d.overallRisk || "Not Evaluated"}
                        </span>
                    </div>
                    <div>
                        <strong>Suggested Action:</strong>
                        <div style={{ padding: '10px', border: '1px dashed #666', marginTop: '5px', background: '#fff' }}>
                            {d.suggested_action || d.suggestedAction || "No action specified."}
                        </div>
                    </div>
                </div>

                <div className="sig-grid">
                    <div className="sig-box">Rater / Clinician Signature</div>
                    <div className="sig-box">Psychiatrist / Supervisor Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}